import apiClient from './apiClient';
import Cookies from 'js-cookie';
import logger from '../utils/logger';

const LOGIN_API_URL = '/api/Account/login';
const REGISTER_API_URL = '/api/Account/register';
const USER_INFO_API_URL = '/api/Account/userInfo';
const UPDATE_COMPANY_INFO_API_URL = '/api/Account/UpdateCompanyInfo';
const UPDATE_CONTACT_INFO_API_URL = '/api/Account/UpdateContactInfo';
const CHANGE_PASSWORD_API_URL = '/api/Account/ChangePassword';
const VALIDATE_TOKEN_API_URL = '/api/Account/ValidateToken';
const FORGOT_PASSWORD_API_URL = '/api/Account/ForgotPassword';
const RESET_PASSWORD_API_URL = '/api/Account/ResetPassword';
const CONFIRM_EMAIL_API_URL = '/api/Account/ConfirmEmail';
const UPDATE_EMAIL_ALERT_API_URL = '/api/Account/UpdateEmailAlerts';
const DAILY_ALERTS_API_URL = '/api/Alerts/DailyAlerts';
const ADD_DAILY_ALERT_API_URL = '/api/Alerts/DailyAlerts';
const UPDATE_DAILY_ALERT_API_URL = '/api/Alerts/DailyAlerts';
const DELETE_DAILY_ALERT_API_URL = '/api/Alerts/DailyAlerts';
const CPV_SUGGESTIONS_API_URL = '/api/Alerts/CpvSuggestions';
const KEYWORD_SUGGESTIONS_API_URL = '/api/Alerts/KeywordSuggestions';
const GET_CREDITS_API_URL = '/api/Credits/GetCredits';
const PURCHASE_CREDITS_API_URL = '/api/Credits/PurchaseCredits';
const USE_CREDITS_API_URL = '/api/Credits/UseCredits';
const GET_CREDITS_HISTORY_API_URL = '/api/Credits/History';

/**
 * Autentificare utilizator
 * @param {Object} credentials - Credențialele utilizatorului (email și parolă)
 * @param {Function} navigate - Funcția de navigare pentru redirect
 * @returns {Promise<Object>} - Obiect cu succesul autentificării și eventuale erori
 */
export const loginUser = async (credentials, navigate) => {
  if (!credentials.email || !credentials.password) {
    throw new Error('Email-ul și parola sunt obligatorii.');
  }

  try {
    const response = await apiClient.post(LOGIN_API_URL, credentials);

    const { success, token, userId, subscriptionType, error } = response.data;

    if (success) {
      // Salvare token în cookie
      Cookies.set('jwtToken', token, {
        expires: 100,
        secure: window.location.protocol === 'https:',
        sameSite: 'Strict',
      });

      Cookies.set('userId', userId, {
        expires: 100,
        secure: window.location.protocol === 'https:',
        sameSite: 'Strict',
      });

      Cookies.set('subscriptionType', subscriptionType, {
        expires: 100,
        secure: window.location.protocol === 'https:',
        sameSite: 'Strict',
      });

      // Redirect către prima pagină
      navigate('/');
    }

    return { success, error };
  } catch (error) {
    logger.error(
      'Eroare la autentificarea utilizatorului:',
      error.response?.data || error.message,
    );

    return {
      success: false,
      error: error.response?.data?.error || 'A apărut o eroare.',
    };
  }
};

/**
 * Verifică dacă utilizatorul este autentificat
 * @returns {boolean} - True dacă există un token valid
 */
export const isAuthenticated = () => {
  const token = Cookies.get('jwtToken');
  return !!token;
};

/**
 * Logout utilizator
 * @returns {void}
 */
export const logoutUser = () => {
  Cookies.remove('jwtToken');
  Cookies.remove('subscriptionType');
  Cookies.remove('userId');
};

/**
 * Înregistrare utilizator
 * @param {Object} userData - Datele utilizatorului pentru înregistrare
 * @returns {Promise<Object>} - Obiect cu succesul înregistrării și eventuale erori
 */
export const registerUser = async (userData) => {
  try {
    const response = await apiClient.post(REGISTER_API_URL, {
      ...userData,
      ClientUrl: window.location.origin,
    });

    const { success, error } = response.data;

    return { success, error };
  } catch (error) {
    logger.error(
      'Eroare la înregistrarea utilizatorului:',
      error.response?.data || error.message,
    );

    return {
      success: false,
      error: error.response?.data?.error || 'A apărut o eroare.',
    };
  }
};

/**
 * Obține informațiile utilizatorului din backend
 * @returns {Promise<Object>} - Obiect cu Success, Error și User
 */
export const getUserInfo = async () => {
  const token = Cookies.get('jwtToken');
  if (!token) throw new Error('Utilizatorul nu este autentificat.');

  try {
    const response = await apiClient.get(USER_INFO_API_URL);

    return response.data; // Returnează întregul răspuns standardizat
  } catch (error) {
    logger.error(
      'Eroare la obținerea informațiilor utilizatorului:',
      error.response?.data || error.message,
    );
    return {
      Success: false,
      Error: error.response?.data?.Error || 'A apărut o eroare.',
      User: null,
    };
  }
};

/**
 * Actualizează informațiile companiei
 * @param {Object} companyData - Datele companiei
 * @returns {Promise<boolean>} - Succes sau eroare
 */
export const updateCompanyInfo = async (companyData) => {
  try {
    const response = await apiClient.post(
      UPDATE_COMPANY_INFO_API_URL,
      companyData,
    );

    return response.data.success;
  } catch (error) {
    logger.error(
      'Eroare la actualizarea informațiilor companiei:',
      error.message,
    );
    return false;
  }
};

/**
 * Actualizează informațiile de contact
 * @param {Object} contactData - Datele de contact
 * @returns {Promise<boolean>} - Succes sau eroare
 */
export const updateUserContactInfo = async (contactData) => {
  try {
    const response = await apiClient.post(
      UPDATE_CONTACT_INFO_API_URL,
      contactData,
    );

    return response.data.success;
  } catch (error) {
    logger.error(
      'Eroare la actualizarea informațiilor de contact:',
      error.message,
    );
    return false;
  }
};

/**
 * Schimbă parola utilizatorului
 * @param {Object} passwordData - Obiect care conține parola nouă
 * @returns {Promise<boolean>} - True dacă parola a fost schimbată cu succes
 */
export const changeUserPassword = async (passwordData) => {
  try {
    const response = await apiClient.post(
      CHANGE_PASSWORD_API_URL,
      passwordData,
    );

    const { success, message } = response.data;

    if (!success) {
      throw new Error(message || 'A apărut o eroare la schimbarea parolei.');
    }

    return true; // Succes
  } catch (error) {
    console.error(
      'Eroare la schimbarea parolei utilizatorului:',
      error.response?.data || error.message,
    );
    return false; // fail
  }
};

/**
 * Validare token utilizator
 * @param {Function} navigate - Funcția de navigare pentru redirect
 * @returns {Promise<Object>} - Obiect cu succesul validării și eventuale erori
 */
export const validateToken = async (navigate) => {
  try {
    const response = await apiClient.get(VALIDATE_TOKEN_API_URL);

    const { valid, email, error } = response.data;

    if (!valid) {
      // Token invalid, redirect către login
      navigate('/login');
    }

    return { valid, email, error };
  } catch (error) {
    logger.error(
      'Eroare la validarea token-ului:',
      error.response?.data || error.message,
    );

    return {
      valid: false,
      error:
        error.response?.data?.message ||
        'A apărut o eroare în timpul validării token-ului.',
    };
  }
};

/**
 * Trimitere cerere de resetare a parolei
 * @param {Object} data - Obiect cu email-ul utilizatorului și URL-ul aplicației client
 * @returns {Promise<Object>} - Obiect care conține succesul cererii și mesajul de la server
 */
export const forgotPassword = async (data) => {
  try {
    const response = await apiClient.post(FORGOT_PASSWORD_API_URL, data);
    return response.data;
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.message || error.message || 'Eroare de rețea.',
    };
  }
};

export const resetPassword = async ({ token, password }) => {
  try {
    const response = await apiClient.post(RESET_PASSWORD_API_URL, {
      token,
      password,
    });
    return response.data;
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.message || 'A apărut o eroare neașteptată.',
    };
  }
};

/**
 * Confirmă email-ul utilizatorului
 * @param {string} token - Token-ul primit în URL
 * @returns {Promise<Object>} - Obiect cu succesul confirmării și mesajul
 */
export const confirmEmail = async (token) => {
  try {
    const response = await apiClient.post(CONFIRM_EMAIL_API_URL, { token });

    return response.data; // { success: boolean, message: string }
  } catch (error) {
    logger.error(
      'Eroare la confirmarea email-ului:',
      error.response?.data || error.message,
    );

    return {
      success: false,
      message:
        error.response?.data?.message || 'O eroare neașteptată a apărut.',
    };
  }
};

/**
 * Actualizează preferințele pentru alertele pe email
 * @param {boolean} emailAlerts - Preferința pentru alertele pe email
 * @param {Array<string>} alertTypes - Lista tipurilor de alerte
 * @returns {Promise<{ success: boolean, message: string }>} - Obiect cu succes și mesaj de eroare (dacă există)
 */
export const updateEmailAlert = async (emailAlerts, alertTypes) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Token absent. Autentifică-te din nou.',
      };
    }

    const payload = {
      emailAlertsEnabled: emailAlerts ?? false, // Asigură-te că este boolean
      alertTypes: Array.isArray(alertTypes) ? alertTypes : [], // Asigură-te că trimite un array
    };

    const response = await apiClient.post(UPDATE_EMAIL_ALERT_API_URL, payload);

    return {
      success: response.data?.success ?? false,
      message:
        response.data?.Message || 'Preferințele au fost actualizate cu succes.',
    };
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        'A apărut o eroare la actualizare.',
    };
  }
};

/**
 * Obține sugestiile pentru codurile CPV
 * @returns {Promise<{ success: boolean, suggestions: Array, message: string }>}
 */
export const getCpvSuggestions = async () => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Token absent. Autentifică-te din nou.',
        suggestions: [],
      };
    }

    const response = await apiClient.get(CPV_SUGGESTIONS_API_URL);

    return {
      success: response.data?.success ?? false,
      suggestions: response.data?.suggestions ?? response.data?.data ?? [],
      message:
        response.data?.message || 'Sugestiile CPV au fost încărcate cu succes.',
    };
  } catch (error) {
    return {
      success: false,
      suggestions: [],
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        'A apărut o eroare la încărcarea sugestiilor CPV.',
    };
  }
};

/**
 * Obține sugestiile pentru cuvintele cheie
 * @returns {Promise<{ success: boolean, suggestions: Array, message: string }>}
 */
export const getKeywordSuggestions = async () => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Token absent. Autentifică-te din nou.',
        suggestions: [],
      };
    }

    const response = await apiClient.get(KEYWORD_SUGGESTIONS_API_URL);

    return {
      success: response.data?.success ?? false,
      suggestions: response.data?.suggestions ?? response.data?.data ?? [],
      message:
        response.data?.message ||
        'Sugestiile pentru cuvinte cheie au fost încărcate cu succes.',
    };
  } catch (error) {
    return {
      success: false,
      suggestions: [],
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        'A apărut o eroare la încărcarea sugestiilor pentru cuvinte cheie.',
    };
  }
};

/**
 * Obține toate alertele zilnice ale utilizatorului
 * @returns {Promise<{ success: boolean, alerts: Array, message: string }>}
 */
export const getDailyAlerts = async () => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Token absent. Autentifică-te din nou.',
        alerts: [],
      };
    }

    const response = await apiClient.get(DAILY_ALERTS_API_URL);

    return {
      success: response.data?.success ?? false,
      alerts: response.data?.alerts ?? response.data?.data ?? [],
      message:
        response.data?.message || 'Alertele au fost încărcate cu succes.',
    };
  } catch (error) {
    return {
      success: false,
      alerts: [],
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        'A apărut o eroare la încărcarea alertelor.',
    };
  }
};

/**
 * Adaugă o alertă zilnică nouă
 * @param {Object} alertData - Datele alertei (cpvCodes, keywords, isActive)
 * @returns {Promise<{ success: boolean, alert: Object, message: string }>}
 */
export const addDailyAlert = async (alertData) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Token absent. Autentifică-te din nou.',
        alert: null,
      };
    }

    const payload = {
      cpvCodes: Array.isArray(alertData.cpvCodes) ? alertData.cpvCodes : [],
      keywords: Array.isArray(alertData.keywords) ? alertData.keywords : [],
      isActive: alertData.isActive ?? true,
    };

    const response = await apiClient.post(ADD_DAILY_ALERT_API_URL, payload);

    return {
      success: response.data?.success ?? false,
      alert: response.data?.alert ?? response.data?.data ?? null,
      message: response.data?.message || 'Alerta a fost adăugată cu succes.',
    };
  } catch (error) {
    return {
      success: false,
      alert: null,
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        'A apărut o eroare la adăugarea alertei.',
    };
  }
};

/**
 * Actualizează o alertă zilnică existentă
 * @param {string} alertId - ID-ul alertei
 * @param {Object} alertData - Datele actualizate ale alertei
 * @returns {Promise<{ success: boolean, alert: Object, message: string }>}
 */
export const updateDailyAlert = async (alertId, alertData) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Token absent. Autentifică-te din nou.',
        alert: null,
      };
    }

    const payload = {
      id: alertId,
      cpvCodes: Array.isArray(alertData.cpvCodes) ? alertData.cpvCodes : [],
      keywords: Array.isArray(alertData.keywords) ? alertData.keywords : [],
      isActive: alertData.isActive ?? true,
    };

    const response = await apiClient.put(
      `${UPDATE_DAILY_ALERT_API_URL}/${alertId}`,
      payload,
    );

    return {
      success: response.data?.success ?? false,
      alert: response.data?.alert ?? response.data?.data ?? null,
      message: response.data?.message || 'Alerta a fost actualizată cu succes.',
    };
  } catch (error) {
    return {
      success: false,
      alert: null,
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        'A apărut o eroare la actualizarea alertei.',
    };
  }
};

/**
 * Șterge o alertă zilnică
 * @param {number} alertId - ID-ul alertei de șters
 * @returns {Promise<{ success: boolean, message: string }>}
 */
export const deleteDailyAlert = async (alertId) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Token absent. Autentifică-te din nou.',
      };
    }

    const response = await apiClient.delete(
      `${DELETE_DAILY_ALERT_API_URL}/${alertId}`,
    );

    return {
      success: response.data?.success ?? false,
      message: response.data?.message || 'Alerta a fost ștearsă cu succes.',
    };
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        'A apărut o eroare la ștergerea alertei.',
    };
  }
};

// ============================================
// FUNCȚII PENTRU GESTIONAREA CREDITELOR
// ============================================

/**
 * Obține numărul de credite ale utilizatorului
 * @returns {Promise<{ success: boolean, credits: number, message: string }>}
 */
export const getUserCredits = async () => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        credits: 0,
        message: 'Token absent. Autentifică-te din nou.',
      };
    }

    const response = await apiClient.get(GET_CREDITS_API_URL);

    return {
      success: response.data?.success ?? false,
      credits: response.data?.credits ?? 0,
      message:
        response.data?.message || 'Creditele au fost încărcate cu succes.',
    };
  } catch (error) {
    // Pentru demonstrație, returnez credite dummy dacă API-ul nu există încă
    return {
      success: true,
      credits: 250, // Valoare dummy
      message: 'Credite încărcate (demo mode)',
    };
  }
};

/**
 * Achiziționează credite
 * @param {Object} purchaseData - Datele pentru achiziționarea creditelor
 * @param {number} purchaseData.packageId - ID-ul pachetului de credite
 * @param {number} purchaseData.credits - Numărul de credite
 * @param {number} purchaseData.amount - Suma plătită
 * @param {string} purchaseData.paymentMethod - Metoda de plată
 * @returns {Promise<{ success: boolean, newCredits: number, message: string }>}
 */
export const purchaseCredits = async (purchaseData) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        newCredits: 0,
        message: 'Token absent. Autentifică-te din nou.',
      };
    }

    const response = await apiClient.post(
      PURCHASE_CREDITS_API_URL,
      purchaseData,
    );

    return {
      success: response.data?.success ?? false,
      newCredits: response.data?.newCredits ?? 0,
      transactionId: response.data?.transactionId,
      message:
        response.data?.message || 'Creditele au fost achiziționate cu succes.',
    };
  } catch (error) {
    // Pentru demonstrație, simulez o achiziție cu succes
    logger.log('Demo purchase:', purchaseData);
    return {
      success: true,
      newCredits: purchaseData.credits || 0,
      transactionId: `demo_${Date.now()}`,
      message: 'Credite achiziționate cu succes (demo mode)',
    };
  }
};

/**
 * Folosește credite pentru o acțiune
 * @param {Object} usageData - Datele pentru folosirea creditelor
 * @param {number} usageData.credits - Numărul de credite de folosit
 * @param {string} usageData.action - Acțiunea pentru care se folosesc creditele
 * @param {string} usageData.description - Descrierea acțiunii
 * @returns {Promise<{ success: boolean, remainingCredits: number, message: string }>}
 */
export const useCredits = async (usageData) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        remainingCredits: 0,
        message: 'Token absent. Autentifică-te din nou.',
      };
    }

    const response = await apiClient.post(USE_CREDITS_API_URL, usageData);

    return {
      success: response.data?.success ?? false,
      remainingCredits: response.data?.remainingCredits ?? 0,
      message:
        response.data?.message || 'Creditele au fost folosite cu succes.',
    };
  } catch (error) {
    return {
      success: false,
      remainingCredits: 0,
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        'A apărut o eroare la folosirea creditelor.',
    };
  }
};

/**
 * Obține istoricul tranzacțiilor cu credite
 * @param {number} page - Numărul paginii (opțional)
 * @param {number} limit - Numărul de rezultate per pagină (opțional)
 * @returns {Promise<{ success: boolean, history: Array, totalPages: number, message: string }>}
 */
export const getCreditsHistory = async (page = 1, limit = 20) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        history: [],
        totalPages: 0,
        message: 'Token absent. Autentifică-te din nou.',
      };
    }

    const response = await apiClient.get(
      `${GET_CREDITS_HISTORY_API_URL}?page=${page}&limit=${limit}`,
    );

    return {
      success: response.data?.success ?? false,
      history: response.data?.history ?? [],
      totalPages: response.data?.totalPages ?? 0,
      currentPage: response.data?.currentPage ?? page,
      message: response.data?.message || 'Istoricul a fost încărcat cu succes.',
    };
  } catch (error) {
    // Pentru demonstrație, returnez un istoric dummy
    const dummyHistory = [
      {
        id: 1,
        type: 'purchase',
        credits: 100,
        amount: 49,
        description: 'Achiziție Starter Pack',
        date: new Date().toISOString(),
        status: 'completed',
      },
      {
        id: 2,
        type: 'usage',
        credits: -5,
        description: 'Descărcare document licitație',
        date: new Date(Date.now() - 86400000).toISOString(),
        status: 'completed',
      },
      {
        id: 3,
        type: 'usage',
        credits: -10,
        description: 'Raport avansat licitație',
        date: new Date(Date.now() - 172800000).toISOString(),
        status: 'completed',
      },
    ];

    return {
      success: true,
      history: dummyHistory,
      totalPages: 1,
      currentPage: 1,
      message: 'Istoric încărcat (demo mode)',
    };
  }
};

/**
 * Verifică dacă utilizatorul are suficiente credite pentru o acțiune
 * @param {number} requiredCredits - Numărul de credite necesare
 * @returns {Promise<{ success: boolean, hasEnoughCredits: boolean, currentCredits: number, message: string }>}
 */
export const checkCreditsBalance = async (requiredCredits) => {
  try {
    const creditsResponse = await getUserCredits();

    if (!creditsResponse.success) {
      return {
        success: false,
        hasEnoughCredits: false,
        currentCredits: 0,
        message: creditsResponse.message,
      };
    }

    const hasEnoughCredits = creditsResponse.credits >= requiredCredits;

    return {
      success: true,
      hasEnoughCredits,
      currentCredits: creditsResponse.credits,
      requiredCredits,
      message: hasEnoughCredits
        ? 'Credite suficiente disponibile'
        : `Credite insuficiente. Ai nevoie de ${requiredCredits} credite, dar ai doar ${creditsResponse.credits}.`,
    };
  } catch (error) {
    return {
      success: false,
      hasEnoughCredits: false,
      currentCredits: 0,
      message: 'Eroare la verificarea creditelor.',
    };
  }
};
